import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { BaseModal } from '@/lib/Modals';
import { CustomConnectorDto } from '@/models/connectors';
import { useCreateConnectorInstanceMutation } from '@/services/endpoints/workspace-connectors';
import Form, { IChangeEvent } from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { FC, FormEvent } from 'react';
import './CustomConnectorForm.css';

interface CustomConnectorAuthModalProps {
  connector: CustomConnectorDto;
  onClose: () => void;
  connectorConnectionInProgress: (status: boolean) => void;
  onChangeConnectionInProgress?: (connector: CustomConnectorDto | null) => void;
}

export const CustomConnectorAuthModal: FC<CustomConnectorAuthModalProps> = ({
  connector,
  onClose,
  connectorConnectionInProgress,
  onChangeConnectionInProgress,
}) => {
  const { id: workspaceId } = useWorkspace();
  const [createInstance, { isLoading: isCreating }] = useCreateConnectorInstanceMutation();

  const authConfig = connector.authConfiguration?.configurations?.[0]?.elements?.auth?.reactJsonSchemaForm;
  const schema = authConfig?.schema;
  const uiSchema = authConfig?.uiSchema;

  const onSubmit = async (data: IChangeEvent<any, RJSFSchema, any>, event: FormEvent<any>) => {
    event.preventDefault();

    connectorConnectionInProgress(true);
    try {
      await createInstance({
        workspaceId,
        connectorId: connector.id,
        name: data.formData.instance_name || `${connector.name} Connection`,
        authParams: data.formData,
      }).unwrap();
      
      onChangeConnectionInProgress?.(connector);
      onClose();
    } catch (error) {
      console.error('Failed to create connector instance:', error);
    } finally {
      connectorConnectionInProgress(false);
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      classNames={{
        dialog: 'max-w-2xl w-full max-h-[90vh] overflow-y-auto',
        content: 'min-h-0 p-0',
      }}
    >
      <div className='custom-connector-form max-h-[80vh] overflow-y-auto'>
        <div className='sticky top-0 z-10 border-b border-primary-border bg-primary-background/95 p-6 pb-4 backdrop-blur-sm'>
          <h2 className='mb-2 text-lg font-medium text-gray-900'>Connect to {connector.name}</h2>
          <p className='text-sm text-gray-600'>Configure your connection settings to authenticate with {connector.name}.</p>
        </div>
        <div className='p-6 pt-4'>
          <Loadable isLoading={isCreating}>
            {schema && <Form schema={schema} uiSchema={uiSchema} validator={validator} onSubmit={onSubmit} />}
            {isCreating && <div>Loading...</div>}
          </Loadable>
        </div>
      </div>
    </BaseModal>
  );
};
